import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useApp } from '@/app/AppContext';
import { formatDateTime, tzAbbrev } from '@/lib/time';
import {
  LegacyImportError,
  analyzeArchive,
  readLegacyZip,
  rejectedReportCsv,
  resolveRowTime,
  unresolvedAmbiguities,
  type AmbiguityChoice,
  type LegacyArchive,
  type ParsedRow,
} from './legacy';
import { commitImport, countAlreadyImported, prepareImport, type ImportResult, type PreparedImport } from './importBatch';

interface Props {
  /** Reports whether the panel holds a draft or work in progress (true) or is idle (false). */
  onEditing?: (editing: boolean) => void;
}

type Phase = 'idle' | 'reading' | 'preview' | 'importing' | 'done';

const TYPE_LABEL = { formula: 'Formula', sleep: 'Sleep', diaper: 'Diaper' } as const;
const SAMPLE_SIZE = 3;
const REJECTED_SHOWN = 20;

function describeValue(row: ParsedRow): string {
  if (row.type === 'formula') return `${row.amount_ml} ml`;
  if (row.type === 'sleep') return `${row.duration_min} min`;
  return row.status ?? '';
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof LegacyImportError) return err.message;
  if (err instanceof Error && err.message) return `${fallback} (${err.message})`;
  return fallback;
}

export function ImportPanel({ onEditing }: Props) {
  const { household, babies, baby: activeBaby } = useApp();
  const [babyId, setBabyId] = useState<string>(activeBaby?.id ?? '');
  const [phase, setPhase] = useState<Phase>('idle');
  const [archive, setArchive] = useState<LegacyArchive | null>(null);
  const [error, setError] = useState('');
  // Kept apart from `error` so a later successful preview never hides a commit failure.
  const [prepareError, setPrepareError] = useState('');
  const [choices, setChoices] = useState<Record<string, AmbiguityChoice>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [prepared, setPrepared] = useState<PreparedImport | null>(null);
  const [existing, setExisting] = useState<number | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const busy = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const target = babies.find((b) => b.id === babyId) ?? null;
  useEffect(() => {
    if (!target && babies.length) setBabyId(activeBaby?.id ?? babies[0].id);
  }, [target, babies, activeBaby]);
  const tz = target?.timezone ?? activeBaby?.timezone ?? 'UTC';

  useEffect(() => {
    setChoices({});
    setConfirmed(false);
    setPrepared(null);
    setExisting(null);
  }, [household?.id, target?.id, tz]);

  const analysis = useMemo(() => (archive ? analyzeArchive(archive, tz) : null), [archive, tz]);
  const unresolved = analysis ? unresolvedAmbiguities(analysis, choices).length : 0;

  // Report draft/busy state; always clear it when the panel goes away.
  const editingCb = useRef(onEditing);
  editingCb.current = onEditing;
  const editing = phase === 'reading' || phase === 'importing' || (archive !== null && phase !== 'done');
  useEffect(() => {
    editingCb.current?.(editing);
  }, [editing]);
  useEffect(() => () => editingCb.current?.(false), []);

  // Deterministic identities and the already-imported count for the preview.
  useEffect(() => {
    if (phase !== 'preview') return;
    setPrepared(null);
    setExisting(null);
    setPrepareError('');
    if (!archive || !analysis || !household || !target || unresolved > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await prepareImport({ archive, analysis, choices, target: { household_id: household.id, baby_id: target.id } });
        const n = await countAlreadyImported(p);
        if (!cancelled) {
          setPrepareError('');
          setPrepared(p);
          setExisting(n);
        }
      } catch (err) {
        if (!cancelled) setPrepareError(errorMessage(err, 'Could not prepare the import.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [archive, analysis, choices, household, target, unresolved, phase]);

  function reset() {
    setArchive(null);
    setChoices({});
    setConfirmed(false);
    setResult(null);
    setError('');
    setPhase('idle');
    if (fileInput.current) fileInput.current.value = '';
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || busy.current) return;
    busy.current = true;
    setArchive(null);
    setChoices({});
    setConfirmed(false);
    setResult(null);
    setError('');
    setPhase('reading');
    try {
      const a = await readLegacyZip(file, file.name);
      setArchive(a);
      setPhase('preview');
    } catch (err) {
      setError(errorMessage(err, 'Could not read the archive.'));
      setPhase('idle');
      if (fileInput.current) fileInput.current.value = '';
    } finally {
      busy.current = false;
    }
  }

  function changeBaby(id: string) {
    setBabyId(id);
    setChoices({});
    setConfirmed(false);
  }

  function choose(ref: string, choice: AmbiguityChoice) {
    setChoices((c) => ({ ...c, [ref]: choice }));
    setConfirmed(false);
  }

  async function confirmImport() {
    if (busy.current || !archive || !analysis || !household || !target || !confirmed || unresolved > 0) return;
    busy.current = true;
    setPhase('importing');
    setError('');
    try {
      // Re-prepare so ids, validation and the batch id are fresh for this commit.
      const p = await prepareImport({ archive, analysis, choices, target: { household_id: household.id, baby_id: target.id } });
      const r = await commitImport(p);
      setPrepared(p);
      setResult(r);
      setPhase('done');
    } catch (err) {
      setError(errorMessage(err, 'Import failed and nothing was saved.'));
      setPhase('preview');
    } finally {
      busy.current = false;
      setConfirmed(false);
    }
  }

  function downloadReport() {
    if (!archive || !analysis) return;
    const csv = rejectedReportCsv(archive, prepared?.rejected ?? analysis.rejected);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    if (typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${archive.name.replace(/\.zip$/i, '')}-rejected-rows.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  if (!household || babies.length === 0)
    return (
      <section className="card space-y-2 p-4" aria-labelledby="import-heading">
        <h2 id="import-heading" className="text-lg font-semibold">Import from Baby Tracker</h2>
        <p className="text-sm text-ink-300">Create a baby profile before importing records.</p>
      </section>
    );

  const rejected = prepared?.rejected ?? analysis?.rejected ?? [];
  const ready = prepared?.rows.length ?? 0;
  const newRows = existing === null ? null : ready - existing;
  const working = phase === 'reading' || phase === 'importing';
  const canImport = phase === 'preview' && confirmed && unresolved === 0 && prepared !== null && !working;

  return (
    <section className="card space-y-4 p-4" aria-labelledby="import-heading" aria-busy={working}>
      <div>
        <h2 id="import-heading" className="text-lg font-semibold">Import from Baby Tracker</h2>
        <p className="mt-1 text-sm text-ink-300">
          Reads a Baby Tracker CSV export (.zip) on this device. Nothing is saved until you confirm, and the original
          file is never changed.
        </p>
      </div>

      <div>
        <label className="label" htmlFor="import-baby">Import into</label>
        <select id="import-baby" className="input" value={target?.id ?? ''} disabled={working} onChange={(e) => changeBaby(e.target.value)}>
          {babies.map((b) => (
            <option key={b.id} value={b.id}>{b.display_name}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-ink-300">Times are day-first (d/M/yy, am/pm) in {tz}. Two-digit years mean 2000–2099. Invalid calendar dates and daylight-saving gaps are rejected.</p>
      </div>

      <div>
        <label className="label" htmlFor="import-file">Baby Tracker export (.zip)</label>
        <input
          ref={fileInput}
          id="import-file"
          className="input"
          type="file"
          accept=".zip,application/zip"
          disabled={working}
          onChange={onFile}
        />
      </div>

      {phase === 'reading' && <p role="status">Reading archive…</p>}
      {error && <p role="alert" className="text-danger">{error}</p>}
      {prepareError && phase === 'preview' && <p role="alert" className="text-danger">{prepareError}</p>}

      {archive && analysis && phase !== 'done' && (
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold">Files in {archive.name}</h3>
            <div className="overflow-x-auto"><table className="mt-2 w-full text-left text-sm">
              <caption className="sr-only">Recognized files and row counts</caption>
              <thead>
                <tr><th scope="col">File</th><th scope="col">Type</th><th scope="col">Rows</th><th scope="col">Valid</th><th scope="col">Rejected</th></tr>
              </thead>
              <tbody>
                {analysis.perFile.map((f) => (
                  <tr key={f.fileId}>
                    <td className="break-all">{f.path}</td><td>{TYPE_LABEL[f.type]}</td><td className="num">{f.total}</td><td className="num">{f.valid}</td><td className="num">{f.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            {analysis.perFile.flatMap((f) => f.warnings.map((w) => <p key={f.fileId + w} className="text-xs text-ink-300">{f.path}: {w}</p>))}
            {archive.skipped.length > 0 && (
              <ul className="mt-2 text-sm text-ink-300" aria-label="Files not imported">
                {archive.skipped.map((s) => <li key={s.path}>Not imported: {s.path} — {s.reason}</li>)}
              </ul>
            )}
            {analysis.sourceBabies.length > 0 && (
              <p className="mt-2 text-sm">Baby name in export: {analysis.sourceBabies.join(', ')}. All rows will be added to {target?.display_name}.</p>
            )}
          </div>

          <div>
            <h3 className="font-semibold">Sample</h3>
            <div className="overflow-x-auto"><table className="mt-2 w-full text-left text-sm">
              <caption className="sr-only">First rows of each file</caption>
              <thead><tr><th scope="col">Type</th><th scope="col">Time ({tz})</th><th scope="col">Value</th><th scope="col">Note</th></tr></thead>
              <tbody>
                {analysis.perFile.flatMap((f) =>
                  analysis.rows.filter((r) => r.fileId === f.fileId).slice(0, SAMPLE_SIZE).map((r) => {
                    const t = resolveRowTime(r, choices);
                    return (
                      <tr key={r.ref}>
                        <td>{TYPE_LABEL[r.type]}</td>
                        <td>{typeof t === 'number' ? formatDateTime(t, tz) : t === 'skip' ? 'Skipped' : 'Needs a choice'}</td>
                        <td>{describeValue(r)}</td>
                        <td className="break-words">{r.note}</td>
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table></div>
          </div>

          {analysis.ambiguous.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">Daylight-saving times to confirm</h3>
              <p className="text-sm text-ink-300">These clock times happened twice when daylight saving ended. Choose which one each row means.</p>
              {analysis.ambiguous.map((r) => {
                if (r.time.kind !== 'ambiguous') return null;
                const [a, b] = r.time.candidates;
                return (
                  <fieldset key={r.ref} className="space-y-1">
                    <legend className="text-sm">{r.path} row {r.row}: {TYPE_LABEL[r.type]} {describeValue(r)} at “{r.rawTime}”</legend>
                    {([['earlier', a], ['later', b]] as const).map(([value, ms]) => (
                      <label key={value} className="mr-4 inline-flex items-center gap-1 text-sm">
                        <input type="radio" name={`dst-${r.ref}`} value={value} disabled={working} checked={choices[r.ref] === value} onChange={() => choose(r.ref, value)} />
                        {value === 'earlier' ? 'Earlier' : 'Later'} ({formatDateTime(ms, tz)} {tzAbbrev(ms, tz)})
                      </label>
                    ))}
                    <label className="inline-flex items-center gap-1 text-sm">
                      <input type="radio" name={`dst-${r.ref}`} value="skip" disabled={working} checked={choices[r.ref] === 'skip'} onChange={() => choose(r.ref, 'skip')} />
                      Don’t import this row
                    </label>
                  </fieldset>
                );
              })}
            </div>
          )}

          {rejected.length > 0 && (
            <div>
              <h3 className="font-semibold">Rejected rows ({rejected.length})</h3>
              <ul className="mt-1 space-y-1 text-sm" aria-label="Rejected rows">
                {rejected.slice(0, REJECTED_SHOWN).map((r) => <li key={r.ref}>{r.path} row {r.row}: {r.reason}</li>)}
              </ul>
              {rejected.length > REJECTED_SHOWN && <p className="text-xs text-ink-300">Showing {REJECTED_SHOWN} of {rejected.length}. Download the report for all.</p>}
            </div>
          )}
          {(rejected.length > 0 || archive.skipped.length > 0) && (
            <button type="button" className="btn-ghost" onClick={downloadReport}>Download rejected-row report</button>
          )}

          <div role="status" aria-live="polite" className="text-sm">
            {unresolved > 0
              ? `${unresolved} daylight-saving time${unresolved === 1 ? '' : 's'} still need a choice.`
              : prepared === null
                ? 'Checking for rows already imported…'
                : `${newRows} new row${newRows === 1 ? '' : 's'} to import, ${existing} already imported, ${rejected.length} rejected${prepared.userSkipped ? `, ${prepared.userSkipped} skipped by you` : ''}.`}
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={confirmed} disabled={working || unresolved > 0 || prepared === null} onChange={(e) => setConfirmed(e.target.checked)} />
            <span>I have reviewed the preview and want to add these rows to {target?.display_name}.</span>
          </label>
          <div className="flex gap-2">
            <button type="button" className="btn-primary" disabled={!canImport} onClick={confirmImport}>
              {phase === 'importing' ? 'Importing…' : 'Import rows'}
            </button>
            <button type="button" className="btn-ghost" disabled={working} onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      {phase === 'done' && result && (
        <div className="space-y-2">
          <p role="status">
            Imported {result.accepted} row{result.accepted === 1 ? '' : 's'}. Skipped {result.alreadyImported} already imported
            {result.userSkipped ? ` and ${result.userSkipped} by your choice` : ''}. Rejected {result.rejected}.
          </p>
          {(result.rejected > 0 || (archive?.skipped.length ?? 0) > 0) && (
            <button type="button" className="btn-ghost" onClick={downloadReport}>Download rejected-row report</button>
          )}
          <button type="button" className="btn-ghost" onClick={reset}>Import another file</button>
        </div>
      )}
    </section>
  );
}
